// SharedCheckoutDrawer.tsx

import { Box, LinearProgress, linearProgressClasses, styled, useMediaQuery, useTheme } from '@mui/material';

import React, { FC, useState } from 'react';

import { CartItem, ShoppingCartItem, UserRetailerShoppingCart } from './type';
import MessageCard from '../../Components/Card/MessageCard';
import { formatMoney, sumAllItemQuantities } from '../../utils';
import pluralize from 'pluralize';
import { brandclubStyles } from '../../styles';
import { Button, CloseButton } from '../../Components';
import { CartProduct } from './CartProduct';
import { ProductRewardSearchResult } from '../../types';
import { ButtonName, createResponsiveStyles, DTCComponentName } from '../../theme';
import { useBagWords } from '../../utils/hooks/useBagWords';
import { bagLineKey, isSameBagLine } from './lineIdentity';
import { lineUnitPrice, type SubscribeAndSaveOffers, type SubscriptionFrequency } from './subscribeAndSave';

const StyledMesssageCard = styled(MessageCard)(({ theme }) => ({
  color: theme.vars.palette.text.primary,
  ...createResponsiveStyles(theme, theme.componentCSSOverrides?.[DTCComponentName.EmptyBagMessageCard]),

  '.store-message-card__cta': {
    backgroundColor: theme.vars.palette.primary.main,
    color: theme.vars.palette.primary.contrastText,
  },
}));

const EmptyBagMessageCard = ({ onActionClick }: { onActionClick: () => void }) => {
  const theme = useTheme();
  const { bag } = useBagWords();

  const customHeaderText = theme.globalComponentPresets?.checkoutDrawer?.customEmptyBagMessageCardHeaderText;

  return (
    <Box sx={{ padding: '20px' }}>
      <StyledMesssageCard
        title={customHeaderText || `Your ${bag} is empty.`}
        content="Can we make some suggestions to help get you started?"
        cta="Shop popular products"
        onActionClick={onActionClick}
      ></StyledMesssageCard>
    </Box>
  );
};

const getTotal = (products: any[], offers?: SubscribeAndSaveOffers) => {
  return products.reduce((acc, item) => {
    return acc + lineUnitPrice(item, offers) * item.quantity;
  }, 0);
};

export const calculateTotalOfAllCarts = (carts: any[], offers?: SubscribeAndSaveOffers) => {
  const total = carts.reduce((acc, cart) => {
    return acc + getTotal(cart.items ?? [], offers);
  }, 0);
  return formatMoney(total);
};

const CheckoutContainer = styled('div', {
  label: 'CheckoutContainer',
})(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  height: '100%',
  overflow: 'hidden',
  backgroundColor: theme.vars.palette.background.paper,
  width: 462,
  maxWidth: '100vw',
  color: theme.vars.palette.text.primary,

  fontFamily: theme.typography.fontFamily,
  '.bc_header_section': {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '23px 28px 10px 28px',
    [theme.breakpoints.down('sm')]: {
      padding: '23px 5% 10px 5%',
    },

    '.bc_header': {
      fontSize: 20,
      fontWeight: 700,
    },
    '.sub_header': {
      fontSize: 18,
      fontWeight: 600,
    },
  },

  '.product_section': {
    flex: 1,
    overflowY: 'scroll',
    ...brandclubStyles.customScrollbar,
    padding: '23px 27px',
    '.cart_product + .cart_product': {
      borderTop: `1px solid #dcdcdc`,
    },
    [theme.breakpoints.down('sm')]: {
      padding: '23px 5%',
    },
    '.cart_group': {
      marginBottom: 50,
      [theme.breakpoints.down('sm')]: {
        marginBottom: 30,
      },
    },
    '.group_info': {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      marginTop: 8,
      marginBottom: 10,
      [theme.breakpoints.down('sm')]: {
        marginTop: 10,
        marginBottom: 14,
      },
      '.group_count': {
        fontSize: 14,
        fontWeight: 500,
      },
      '.group_subtotal': {
        fontSize: 14,
        fontWeight: 500,
        '.bc-dtc-price, .price': {
          fontWeight: 600,
        },
      },
    },
  },

  '.checkout_section': {
    display: 'flex',
    flexDirection: 'column',
    gap: '23px',
    padding: '23px 27px 37px 27px',

    boxShadow: '0 0 6px 0 rgba(119, 115, 115, 0.16)',
    [theme.breakpoints.down('sm')]: {
      padding: theme.globalComponentPresets?.checkoutDrawer?.checkoutDrawerHeaderButton
        ? '23px 5% 23px 5%'
        : '23px 5% 37px 5%',

      boxShadow: theme.globalComponentPresets?.checkoutDrawer?.checkoutDrawerHeaderButton
        ? `0 4px 6px -2px rgba(119, 115, 115, 0.16)`
        : '0 0 6px 0 rgba(119, 115, 115, 0.16)',
    },

    '.subtotal': {
      display: 'flex',
      justifyContent: 'space-between',
      width: '100%',
      fontSize: 18,
      fontWeight: 600,
      color: theme.vars.palette.text.primary,
    },
  },
}));

const StyledEmptyBagMessageCard = styled(EmptyBagMessageCard)({
  width: 'auto',
  margin: '0 25px',
  marginTop: 50,
  '.store-message-card__content': {
    fontSize: 16,
    width: 282,
  },
  '.store-message-card__title': {
    fontSize: 20,
  },
  '.store-message-card__cta': {
    marginTop: 10,
  },
});

interface ProductSectionProps {
  loading: boolean;
  allCarts: UserRetailerShoppingCart[] | undefined;
  LoadingComponent?: FC;
  cartItems: CartItem[];
  storeBrandingType: string;
  cartProducts?: { [retailerSku: string]: ProductRewardSearchResult };
  handleAddQuantity: (product: ShoppingCartItem) => void;
  handleMinusQuantity: (product: ShoppingCartItem) => void;
  subscriptionOffers?: SubscribeAndSaveOffers;
  handleChangeSubscription?: (product: ShoppingCartItem, frequency: SubscriptionFrequency) => void;
}

const getAllSortedProducts = (cartItems: CartItem[], allCarts: UserRetailerShoppingCart[]) => {
  const allItems: ShoppingCartItem[] = allCarts.flatMap((cart) => cart.items);

  const sortedProducts = allItems.sort((a, b) => {
    const aCreatedTime = cartItems.find((item) => isSameBagLine(item, a))?.createdTime;
    const bCreatedTime = cartItems.find((item) => isSameBagLine(item, b))?.createdTime;

    // If both have no createdTime, maintain original order
    if (!aCreatedTime && !bCreatedTime) return 0;

    // If only a has no createdTime, a comes first (negative value)
    if (!aCreatedTime) return -1;

    // If only b has no createdTime, b comes first (positive value)
    if (!bCreatedTime) return 1;

    // Both have createdTime, sort by newest first

    return bCreatedTime - aCreatedTime;
  });

  return sortedProducts;
};

const ProductSection: FC<ProductSectionProps> = ({
  loading,
  allCarts,
  cartItems,
  LoadingComponent,
  storeBrandingType,
  cartProducts,
  handleAddQuantity,
  handleMinusQuantity,
  subscriptionOffers,
  handleChangeSubscription,
}) => {
  if (loading) {
    return <Box className="product_section">{LoadingComponent ? <LoadingComponent /> : <div>Loading...</div>}</Box>;
  }

  if (!allCarts || allCarts.length === 0) {
    return null;
  }

  const sortedProducts = getAllSortedProducts(cartItems, allCarts);
  const primeOf = (product: ShoppingCartItem) => {
    const primeDelivery = allCarts.find((cart) => cart.retailerId === product.retailerId)?.primeDelivery;
    const line = primeDelivery?.items.find((item) => item.retailerSku === product.retailerSku);
    return primeDelivery && line ? { targetId: primeDelivery.targetId, amazonSku: line.amazonSku } : undefined;
  };

  return (
    <Box className="product_section">
      <Box className="group_info">
        <Box className="group_count">{pluralize('item', sumAllItemQuantities(allCarts), true)}</Box>
        <Box className="group_subtotal">
          Subtotal: <span className="bc-dtc-price">{calculateTotalOfAllCarts(allCarts, subscriptionOffers)}</span>
        </Box>
      </Box>
      {sortedProducts.map((product) => (
        <CartProduct
          key={bagLineKey(product)}
          product={product}
          prime={primeOf(product)}
          cartProducts={cartProducts}
          storeBrandingType={storeBrandingType}
          handleAddQuantity={handleAddQuantity}
          handleMinusQuantity={handleMinusQuantity}
          subscriptionOffer={subscriptionOffers?.[product.stacklineSku]}
          handleChangeSubscription={handleChangeSubscription}
        />
      ))}
    </Box>
  );
};

interface CheckoutSectionProps {
  canCheckout: boolean;
  initiating: boolean;
  allCarts: UserRetailerShoppingCart[] | undefined;
  closeDrawer: () => void;
  onCheckout: (allCarts: UserRetailerShoppingCart[]) => Promise<void>;
  onViewBag: () => void;
  closeAfterCheckout?: boolean;
}

const CheckoutSection: FC<CheckoutSectionProps> = ({
  initiating,
  closeDrawer,
  canCheckout,
  allCarts = [],
  onCheckout,
  onViewBag,
  closeAfterCheckout = true,
}) => {
  const theme = useTheme();
  const customCheckoutText = theme.globalComponentPresets?.checkoutDrawer?.customCheckoutText;
  const customViewBagText = theme.globalComponentPresets?.checkoutDrawer?.customViewBagText;
  // A theme's own text wins. Otherwise use the brand's bag word.
  const { bag } = useBagWords();

  const [localInitiating, setLocalInitiating] = useState(false);

  const handleCheckout = async () => {
    if (allCarts.length > 0) {
      try {
        setLocalInitiating(true);
        await onCheckout(allCarts);
      } finally {
        if (closeAfterCheckout) {
          closeDrawer();
        }
      }
    }
  };

  return (
    <Box className="checkout_section">
      <Box display="flex" gap="12px" width="100%" flexDirection="column" alignItems="center">
        {canCheckout ? (
          <Button
            className="bc-checkout-drawer-checkout-button"
            increaseSpecificity
            variant="solid"
            color="primary"
            fullWidth
            buttonName={ButtonName.BagDrawerCheckoutButton}
            disabled={initiating || localInitiating}
            onClick={handleCheckout}
            disableMaxWidth={true}
            sx={{
              width: '100%',
            }}
          >
            {customCheckoutText || 'Checkout'}
          </Button>
        ) : null}
        <Button
          className="bc-checkout-drawer-view-bag-button"
          increaseSpecificity
          fullWidth
          variant="outline"
          color="primary"
          buttonName={ButtonName.BagDrawerViewBagButton}
          onClick={onViewBag}
          disableMaxWidth={true}
          sx={{
            width: '100%',
          }}
        >
          {customViewBagText || `View ${bag}`}
        </Button>
      </Box>
    </Box>
  );
};

interface CheckoutDrawerProps {
  loading: boolean;
  cartItems: CartItem[];
  showFreeShippingOffer?: boolean;
  allCarts: UserRetailerShoppingCart[] | undefined;
  closeDrawer: () => void;
  onCheckout: (allCarts: UserRetailerShoppingCart[]) => Promise<void>;
  onViewBag: () => void;
  onEmptyBagAction?: () => void;
  LoadingComponent?: FC;
  initiating?: boolean;
  storeBrandingType: string;
  cartProducts?: { [retailerSku: string]: ProductRewardSearchResult };
  handleAddQuantity: (product: ShoppingCartItem) => void;
  handleMinusQuantity: (product: ShoppingCartItem) => void;
  closeAfterCheckout?: boolean;
  /** The offers of the subscription lines, by stackline sku. The bag shows those lines at the subscriber price. */
  subscriptionOffers?: SubscribeAndSaveOffers;
  /** Sets the frequency of a subscription line. A host without it shows no controls. */
  handleChangeSubscription?: (product: ShoppingCartItem, frequency: SubscriptionFrequency) => void;
}

const BorderLinearProgress = styled(LinearProgress)(({ theme }) => ({
  height: 10,
  borderRadius: 5,
  [`&.${linearProgressClasses.colorPrimary}`]: {
    backgroundColor: theme.palette.grey[200],
  },
  [`& .${linearProgressClasses.bar}`]: {
    borderRadius: 5,
    backgroundColor: theme.vars.palette.primary.main,
  },
}));

const StyledFreeShippingCap = styled(Box)(({ theme }) => ({
  color: theme.vars.palette.text.primary,
  padding: '23px 27px 0 27px',
  boxSizing: 'border-box',
  [theme.breakpoints.down('sm')]: {
    padding: '23px 5%',
  },
  '.free-shipping-cap-text': {
    fontSize: 14,
    fontWeight: 500,
    marginBottom: 6,
  },

  '.free-shipping-text': {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    fontSize: 12,
    fontWeight: 500,
    color: theme.vars.palette.text.primary,
  },
}));

const FreeShippingOffer = ({
  allCarts,
  freeShippingCap,
  offers,
}: {
  allCarts?: UserRetailerShoppingCart[];
  freeShippingCap: number;
  offers?: SubscribeAndSaveOffers;
}) => {
  const total =
    allCarts?.reduce((acc, cart) => {
      return acc + getTotal(cart.items ?? [], offers);
    }, 0) || 0;

  const isFreeShipping = total >= freeShippingCap;

  const remainingAmount = freeShippingCap - total;

  if (total === 0) {
    return null;
  }

  if (isFreeShipping) {
    return (
      <StyledFreeShippingCap>
        <Box className="free-shipping-cap-text">You're getting Free Standard Shipping!</Box>
      </StyledFreeShippingCap>
    );
  }

  return (
    <StyledFreeShippingCap>
      <Box className="free-shipping-cap-text">
        You're {formatMoney(remainingAmount)} away from Free Standard Shipping
      </Box>
      <BorderLinearProgress variant="determinate" value={(total / freeShippingCap) * 100} />
      <Box className="free-shipping-text">
        <div>{formatMoney(0)}</div>
        <div>{formatMoney(freeShippingCap)}</div>
      </Box>
    </StyledFreeShippingCap>
  );
};

const StatusMessageBanner = styled(Box)(({ theme }) => ({
  margin: '0 28px 16px 28px',
  padding: '12px 14px',
  borderRadius: 12,
  backgroundColor: theme.vars.palette.warning.light,
  color: theme.vars.palette.warning.contrastText,
  fontSize: 14,
  fontWeight: 500,
  [theme.breakpoints.down('sm')]: {
    margin: '0 5% 16px 5%',
  },
}));

export const CheckoutDrawer: FC<CheckoutDrawerProps> = ({
  loading,
  cartItems,
  showFreeShippingOffer = false,
  allCarts,
  storeBrandingType,
  cartProducts,
  handleAddQuantity,
  handleMinusQuantity,
  closeDrawer,
  onCheckout,
  onViewBag,
  onEmptyBagAction,
  LoadingComponent,
  initiating = false,
  closeAfterCheckout = true,
  subscriptionOffers,
  handleChangeSubscription,
}) => {
  const cartsWithItems = (allCarts || []).filter((cart) => (cart.items?.length ?? 0) > 0);
  const hasItems = cartsWithItems.length > 0;
  const statusMessages = [...new Set((allCarts || []).map((cart) => cart.statusMessage).filter(Boolean))];

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const checkoutDrawerHeaderButton = theme.globalComponentPresets?.checkoutDrawer?.checkoutDrawerHeaderButton;

  const customHeaderName = theme.globalComponentPresets?.checkoutDrawer?.customHeaderName;
  const { Bag: BagTitle } = useBagWords();

  return (
    <CheckoutContainer className="bc-checkout-drawer">
      <Box className="bc_header_section">
        <Box className="bc_header">{customHeaderName || BagTitle}</Box>
        <CloseButton
          style={{
            all: 'unset',
            cursor: 'pointer',
          }}
          className="options"
          onClick={closeDrawer}
          iconSize={15}
        />
      </Box>
      {showFreeShippingOffer ? <FreeShippingOffer allCarts={allCarts} freeShippingCap={35} offers={subscriptionOffers} /> : null}
      {statusMessages.map((message) => (
        <StatusMessageBanner key={message}>{message}</StatusMessageBanner>
      ))}

      {!hasItems && !loading ? (
        <StyledEmptyBagMessageCard onActionClick={onEmptyBagAction || closeDrawer} />
      ) : (
        <>
          {hasItems && checkoutDrawerHeaderButton && isMobile ? (
            <CheckoutSection
              canCheckout={cartsWithItems.length === 1}
              initiating={initiating}
              allCarts={cartsWithItems}
              closeDrawer={closeDrawer}
              onCheckout={onCheckout}
              onViewBag={onViewBag}
              closeAfterCheckout={closeAfterCheckout}
            />
          ) : null}

          <ProductSection
            storeBrandingType={storeBrandingType}
            cartProducts={cartProducts}
            handleAddQuantity={handleAddQuantity}
            handleMinusQuantity={handleMinusQuantity}
            loading={loading}
            allCarts={cartsWithItems}
            cartItems={cartItems}
            LoadingComponent={LoadingComponent}
            subscriptionOffers={subscriptionOffers}
            handleChangeSubscription={handleChangeSubscription}
          />
          {hasItems && !(checkoutDrawerHeaderButton && isMobile) && (
            <CheckoutSection
              canCheckout={cartsWithItems.length === 1}
              initiating={initiating}
              allCarts={cartsWithItems}
              closeDrawer={closeDrawer}
              onCheckout={onCheckout}
              onViewBag={onViewBag}
              closeAfterCheckout={closeAfterCheckout}
            />
          )}
        </>
      )}
    </CheckoutContainer>
  );
};

export default CheckoutDrawer;
